import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { croppedServiceImages, serviceImageFor } from '../content/serviceImages';

// Share of the row (in %) taken by the enlarged card, and the gap between cards.
const ACTIVE_SIZE = 60;
const GAP = 1;

// How each card sits in the pile: [rotation in deg, x offset in px, y offset in px].
const STACK_OFFSETS = [
  [0, 0, 0],
  [-2.5, -16, 6],
  [2.5, 16, 4],
  [-4.5, -26, 10],
  [4.5, 26, 8],
  [-6.5, -34, 14],
  [6.5, 34, 12],
];

// Only keyboard focus opens the deck; a click or tap on a card is handled by its click.
function isKeyboardFocus(node) {
  try {
    return node.matches(':focus-visible');
  } catch {
    return false; // browsers without :focus-visible
  }
}

// Where each card sits along the row (top to bottom on narrow screens), in %.
function rowLayout(count, active) {
  const free = 100 - GAP * (count - 1);
  let start = 0;
  return Array.from({ length: count }, (_, i) => {
    let size = free / count;
    if (active !== null) size = i === active ? ACTIVE_SIZE : (free - ACTIVE_SIZE) / (count - 1);
    const slot = { start, size };
    start += size + GAP;
    return slot;
  });
}

// Service cards piled on top of each other; they spread into a row on hover (or on a tap),
// and clicking one enlarges it while the others shrink.
function ServiceDeck({ services, hint, touchHint, moreLabel, label }) {
  const deckRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const close = () => {
    setOpen(false);
    setActive(null);
  };

  // Touch screens have no hover to leave, so a tap outside the deck closes it.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (deckRef.current && !deckRef.current.contains(event.target)) {
        setOpen(false);
        setActive(null);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const handleCardClick = (index) => {
    // A tap on the closed pile only spreads it; the next tap picks a card.
    if (!open) {
      setOpen(true);
      return;
    }
    setActive((current) => (current === index ? null : index));
  };

  const slots = rowLayout(services.length, active);
  let deckState = 'is-stacked';
  if (open) deckState = active === null ? 'is-spread' : 'has-active';

  return (
    <div
      ref={deckRef}
      className={`svc-deck ${deckState}`}
      role="group"
      aria-label={label}
      onPointerEnter={(event) => event.pointerType !== 'touch' && setOpen(true)}
      onPointerLeave={(event) => event.pointerType !== 'touch' && close()}
      onFocus={(event) => isKeyboardFocus(event.target) && setOpen(true)}
      onBlur={(event) => {
        if (!deckRef.current?.contains(event.relatedTarget)) close();
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        if (active !== null) setActive(null);
        else close();
      }}
    >
      {services.map((service, i) => {
        const image = service.image || serviceImageFor(service.id);
        const isActive = active === i;
        const [tilt, dx, dy] = STACK_OFFSETS[i % STACK_OFFSETS.length];
        let cardState = '';
        if (active !== null) cardState = isActive ? 'is-active' : 'is-shrunk';
        return (
          <article
            key={`${service.id}-${service.name}`}
            className={`svc-deck-card ${cardState} ${croppedServiceImages.has(image) ? 'svc-deck-card-crop' : ''}`}
            style={{
              '--i': i,
              '--start': `${slots[i].start}%`,
              '--size': `${slots[i].size}%`,
              '--tilt': `${tilt}deg`,
              '--dx': `${dx}px`,
              '--dy': `${dy}px`,
              backgroundImage: `url(${image})`,
            }}
          >
            <button
              type="button"
              className="svc-deck-hit"
              onClick={() => handleCardClick(i)}
              aria-expanded={isActive}
              aria-label={service.name}
            />
            <span className="svc-deck-num" aria-hidden="true">{service.id}</span>
            {i === 0 && (
              <span className="svc-deck-hint" aria-hidden="true">
                <span className="material-symbols-outlined">touch_app</span>
                <span className="svc-deck-hint-hover">{hint}</span>
                <span className="svc-deck-hint-touch">{touchHint}</span>
              </span>
            )}
            {/* The images carry the service names, so the card only adds a Learn more button
                once enlarged; the name stays for screen readers. */}
            <div className="svc-deck-body">
              <h3 className="svc-deck-name">{service.name}</h3>
              <div className="svc-deck-more" aria-hidden={!isActive}>
                <div>
                  <Link to={`/services/${service.id}`} className="sd-know-more-btn" tabIndex={isActive ? 0 : -1}>
                    {moreLabel}
                    <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
                  </Link>
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default ServiceDeck;
